import React, { useState } from 'react'

import {
    Modal,
    Slide,
    Paper,
    Button,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    TablePagination,
    Typography,
    TextField,
    IconButton,
    Box,
    makeStyles,
    useTheme,
} from '@material-ui/core'
import AddIcon from '@material-ui/icons/Add'
import DeleteIcon from '@material-ui/icons/Delete'
import GetAppIcon from '@material-ui/icons/GetApp'

import { DropFiles } from '../../components/DropFiles/DropFiles'

interface BulkDamageConfig {
    description: string
    cost: number
    integration_id: string | null
    charge_item_description: string | null
}

interface BulkDamageConfigModalProps {
    open: boolean
    onClose: () => void
    onSubmit: (configs: BulkDamageConfig[]) => void
}

const useStyles = makeStyles((theme) => ({
    modalContent: {
        width: '90%',
        maxWidth: 1500,
        height: '90%',
        backgroundColor: theme.palette.background.paper,
        boxShadow: theme.shadows[24],
        padding: theme.spacing(2),
        display: 'flex',
        flexDirection: 'column',
        overflow: 'auto',
        borderRadius: theme.shape.borderRadius,
    },
    header: {
        marginBottom: theme.spacing(2),
        display: 'flex',
        flexDirection: 'column',
        gap: theme.spacing(1),
    },
    headerRow: {
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
    },
    title: {
        fontWeight: 500,
        color: theme.palette.text.primary,
    },
    subtitle: {
        color: theme.palette.text.secondary,
    },
    uploadSection: {
        marginBottom: theme.spacing(1),
        display: 'flex',
        flexDirection: 'column',
        gap: theme.spacing(2),
    },
    uploadOptions: {
        display: 'flex',
        gap: theme.spacing(1),
        marginBottom: theme.spacing(1),
    },
    uploadOption: {
        flex: 1,
        padding: theme.spacing(2),
        border: `1px solid ${theme.palette.divider}`,
        borderRadius: theme.shape.borderRadius,
        cursor: 'pointer',
        transition: 'all 0.2s ease',
        '&:hover': {
            backgroundColor: theme.palette.action.hover,
            borderColor: theme.palette.primary.main,
        },
    },
    uploadOptionSelected: {
        borderColor: theme.palette.primary.main,
        backgroundColor: theme.palette.action.hover,
    },
    uploadIcon: {
        marginBottom: theme.spacing(1),
        color: theme.palette.primary.main,
    },
    uploadText: {
        textAlign: 'center',
        color: theme.palette.text.secondary,
    },
    dropZone: {
        border: `2px dashed ${theme.palette.divider}`,
        borderRadius: theme.shape.borderRadius,
        padding: theme.spacing(2),
        backgroundColor: theme.palette.action.hover,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        minHeight: 120,
    },
    tableContainer: {
        flex: 1,
        marginTop: theme.spacing(2),
        cursor: 'text',
        outline: 'none',
        boxShadow: 'none',
        border: `1px solid ${theme.palette.divider}`,
        borderRadius: theme.shape.borderRadius,
    },
    tableHead: {
        backgroundColor: theme.palette.background.default,
    },
    tableHeadCell: {
        fontWeight: 500,
        color: theme.palette.text.primary,
        backgroundColor: theme.palette.background.default,
    },
    tableRow: {
        '&:hover': {
            backgroundColor: theme.palette.action.hover,
        },
    },
    actionButtons: {
        marginTop: theme.spacing(1),
        display: 'flex',
        justifyContent: 'flex-end',
        alignItems: 'center',
        gap: theme.spacing(2),
        paddingTop: theme.spacing(2),
        borderTop: `1px solid ${theme.palette.divider}`,
    },
    totalCount: {
        marginRight: 'auto',
        color: theme.palette.text.secondary,
    },
    emptyState: {
        padding: theme.spacing(4),
        textAlign: 'center',
        color: theme.palette.text.secondary,
    },
    textField: {
        '& .MuiOutlinedInput-root': {
            backgroundColor: theme.palette.background.paper,
        },
        '& .MuiInputAdornment-root': {
            marginRight: theme.spacing(0.5),
            '& .MuiTypography-root': {
                color: theme.palette.text.secondary,
            },
        },
    },
    dropZoneContainer: {
        display: 'flex',
        flexDirection: 'column',
        gap: theme.spacing(2),
    },
    dropZoneActions: {
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        gap: theme.spacing(2),
    },
    downloadButton: {
        textTransform: 'none',
    },
}))

const emptyConfig: BulkDamageConfig = {
    description: '',
    cost: 0,
    integration_id: null,
    charge_item_description: null,
}

const ConfigRow: React.FC<{
    config: BulkDamageConfig
    isNew?: boolean
    onUpdate?: (field: keyof BulkDamageConfig, value: any) => void
    onDelete?: () => void
    onAdd?: () => void
    onKeyPress?: (e: React.KeyboardEvent) => void
    disabled?: boolean
}> = ({ config, isNew, onUpdate, onDelete, onAdd, onKeyPress, disabled }) => {
    const classes = useStyles()
    const theme = useTheme()

    return (
        <TableRow className={classes.tableRow}>
            <TableCell>
                <TextField
                    fullWidth
                    size="small"
                    placeholder="Description"
                    value={config.description}
                    onChange={(e) => onUpdate?.('description', e.target.value)}
                    onKeyDown={onKeyPress}
                    className={classes.textField}
                    disabled={disabled}
                />
            </TableCell>
            <TableCell>
                <TextField
                    fullWidth
                    size="small"
                    type="number"
                    placeholder="Cost"
                    value={config.cost}
                    onChange={(e) => onUpdate?.('cost', Number(e.target.value))}
                    onKeyDown={onKeyPress}
                    className={classes.textField}
                    disabled={disabled}
                    InputProps={{
                        startAdornment: (
                            <Typography
                                component="span"
                                style={{ marginRight: 4 }}
                            >
                                $
                            </Typography>
                        ),
                    }}
                />
            </TableCell>
            <TableCell>
                <TextField
                    fullWidth
                    size="small"
                    placeholder="Charge Item Description"
                    value={config.charge_item_description || ''}
                    onChange={(e) =>
                        onUpdate?.(
                            'charge_item_description',
                            e.target.value || null,
                        )
                    }
                    onKeyDown={onKeyPress}
                    className={classes.textField}
                    disabled={disabled}
                />
            </TableCell>
            <TableCell>
                <TextField
                    fullWidth
                    size="small"
                    placeholder="Integration Code"
                    value={config.integration_id || ''}
                    onChange={(e) =>
                        onUpdate?.('integration_id', e.target.value || null)
                    }
                    onKeyDown={onKeyPress}
                    className={classes.textField}
                    disabled={disabled}
                />
            </TableCell>
            <TableCell>
                {isNew ? (
                    <IconButton
                        size="small"
                        onClick={onAdd}
                        disabled={disabled || !config.description.trim()}
                        color="primary"
                    >
                        <AddIcon fontSize="small" />
                    </IconButton>
                ) : (
                    <IconButton
                        size="small"
                        onClick={onDelete}
                        style={{ color: theme.palette.error.main }}
                    >
                        <DeleteIcon fontSize="small" />
                    </IconButton>
                )}
            </TableCell>
        </TableRow>
    )
}

export const BulkDamageConfigModal: React.FC<BulkDamageConfigModalProps> = ({
    open,
    onClose,
    onSubmit,
}) => {
    const classes = useStyles()
    const [configs, setConfigs] = useState<BulkDamageConfig[]>([])
    const [newConfig, setNewConfig] = useState<BulkDamageConfig>(emptyConfig)
    const [page, setPage] = useState(0)
    const [rowsPerPage, setRowsPerPage] = useState(50)

    const handlePaste = (e: React.ClipboardEvent) => {
        e.preventDefault()
        const pastedData = e.clipboardData.getData('text')
        const rows = pastedData.split('\n').filter((row) => row.trim())

        const parsedConfigs = rows.map((row) => {
            const [
                description,
                cost,
                charge_item_description,
                integration_id,
            ] = row.split('\t')
            return {
                description: description?.trim() || '',
                cost: Number(cost) || 0,
                integration_id: integration_id?.trim() || null,
                charge_item_description:
                    charge_item_description?.trim() || null,
            }
        })

        setConfigs([...configs, ...parsedConfigs])
    }

    const handleClose = () => {
        setConfigs([])
        setNewConfig(emptyConfig)
        onClose()
    }

    const handleSubmit = () => {
        onSubmit(configs)
        handleClose()
    }

    const handleAddRow = () => {
        if (newConfig.description.trim()) {
            setConfigs([...configs, newConfig])
            setNewConfig(emptyConfig)
        }
    }

    const handleDeleteRow = (index: number) => {
        setConfigs(configs.filter((_, i) => i !== index))
    }

    const handleKeyPress = (e: React.KeyboardEvent) => {
        if (e.key === 'Enter') {
            handleAddRow()
        }
    }

    const handleUpdateConfig = (
        index: number,
        field: keyof BulkDamageConfig,
        value: any,
    ) => {
        setConfigs(
            configs.map((config, i) =>
                i === index ? { ...config, [field]: value } : config,
            ),
        )
    }

    const handleUpdateNewConfig = (
        field: keyof BulkDamageConfig,
        value: any,
    ) => {
        setNewConfig({ ...newConfig, [field]: value })
    }

    const handleFileDrop = (files: File[]) => {
        const file = files[0]
        const reader = new FileReader()

        reader.onload = (e) => {
            try {
                const text = e.target?.result as string
                const rows = text
                    .split('\n')
                    .map((row) => row.split(',').map((cell) => cell.trim()))

                const parsedConfigs = rows.slice(1).map((row) => ({
                    description: row[0] || '',
                    cost: Number(row[1]) || 0,
                    charge_item_description: row[2] || null,
                    integration_id: row[3] || null,
                }))

                setConfigs([...configs, ...parsedConfigs])
            } catch (error) {
                console.error('Error parsing CSV file:', error)
            }
        }

        reader.readAsText(file)
    }

    const handleDownloadExample = () => {
        const element = document.createElement('a')
        const file = new Blob([EXAMPLE_CSV], {
            type: 'text/csv;charset=utf-8;',
        })
        element.href = URL.createObjectURL(file)
        element.download = 'bulk_damage_configs.csv'
        document.body.appendChild(element)
        element.click()
        document.body.removeChild(element)
    }

    const displayedConfigs = configs.slice(
        page * rowsPerPage,
        page * rowsPerPage + rowsPerPage,
    )

    return (
        <Modal
            open={open}
            onClose={handleClose}
            closeAfterTransition
            style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
            }}
        >
            <Slide direction="up" in={open}>
                <Paper className={classes.modalContent}>
                    <div className={classes.header}>
                        <div className={classes.headerRow}>
                            <div>
                                <Typography
                                    variant="h5"
                                    className={classes.title}
                                >
                                    Bulk Add Damage Configs
                                </Typography>
                                <Typography
                                    variant="body2"
                                    className={classes.subtitle}
                                >
                                    Upload a CSV file or paste data from Excel
                                </Typography>
                            </div>
                            <Button
                                startIcon={<GetAppIcon />}
                                onClick={handleDownloadExample}
                                color="primary"
                                className={classes.downloadButton}
                            >
                                Download Example CSV
                            </Button>
                        </div>
                    </div>

                    <div className={classes.dropZoneContainer}>
                        <div className={classes.dropZone}>
                            <DropFiles
                                displayString="Drop your CSV file here or click to browse"
                                fileTypes={{
                                    'text/csv': ['.csv'],
                                }}
                                multipleFiles={false}
                                onDrop={handleFileDrop}
                            />
                        </div>
                    </div>

                    <Typography
                        variant="body2"
                        className={classes.subtitle}
                        style={{ marginTop: '16px' }}
                    >
                        Or add configurations manually below
                    </Typography>

                    <TableContainer
                        component={Paper}
                        onPaste={handlePaste}
                        tabIndex={0}
                        className={classes.tableContainer}
                    >
                        <Table size="small" stickyHeader>
                            <TableHead className={classes.tableHead}>
                                <TableRow>
                                    <TableCell
                                        className={classes.tableHeadCell}
                                    >
                                        Description (Required)
                                    </TableCell>
                                    <TableCell
                                        className={classes.tableHeadCell}
                                    >
                                        Cost (Required)
                                    </TableCell>
                                    <TableCell
                                        className={classes.tableHeadCell}
                                    >
                                        Charge Item Description
                                    </TableCell>
                                    <TableCell
                                        className={classes.tableHeadCell}
                                    >
                                        Integration Code
                                    </TableCell>
                                    <TableCell
                                        className={classes.tableHeadCell}
                                        width="50px"
                                    ></TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {displayedConfigs.map((config, index) => (
                                    <ConfigRow
                                        key={index}
                                        config={config}
                                        onUpdate={(field, value) =>
                                            handleUpdateConfig(
                                                page * rowsPerPage + index,
                                                field,
                                                value,
                                            )
                                        }
                                        onDelete={() =>
                                            handleDeleteRow(
                                                page * rowsPerPage + index,
                                            )
                                        }
                                    />
                                ))}
                                <ConfigRow
                                    config={newConfig}
                                    isNew
                                    onUpdate={handleUpdateNewConfig}
                                    onAdd={handleAddRow}
                                    onKeyPress={handleKeyPress}
                                />
                                {configs.length === 0 && (
                                    <TableRow>
                                        <TableCell
                                            colSpan={5}
                                            className={classes.emptyState}
                                        >
                                            Your damage configurations will
                                            appear here
                                        </TableCell>
                                    </TableRow>
                                )}
                            </TableBody>
                        </Table>
                    </TableContainer>

                    <TablePagination
                        rowsPerPageOptions={[5, 10, 25, 50]}
                        component="div"
                        count={configs.length}
                        rowsPerPage={rowsPerPage}
                        page={page}
                        onChangePage={(_, newPage) => setPage(newPage)}
                        onChangeRowsPerPage={(e) => {
                            setRowsPerPage(parseInt(e.target.value, 10))
                            setPage(0)
                        }}
                    />

                    <Box className={classes.actionButtons}>
                        <Typography
                            variant="body2"
                            className={classes.totalCount}
                        >
                            Total Configs: {configs.length}
                        </Typography>
                        <Button
                            onClick={() => setConfigs([])}
                            color="secondary"
                            disabled={configs.length === 0}
                        >
                            Clear All
                        </Button>
                        <Button onClick={handleClose} color="primary">
                            Cancel
                        </Button>
                        <Button
                            onClick={handleSubmit}
                            color="primary"
                            variant="contained"
                            disabled={configs.length === 0}
                        >
                            Add Configs
                        </Button>
                    </Box>
                </Paper>
            </Slide>
        </Modal>
    )
}

const EXAMPLE_CSV = `Description (Required),Cost (Required),Charge Item Description,Integration Code
Wall Hole Repair,75.00,Wall Hole Repair and Patch,
Carpet Stain,150.00,Carpet Stain Removal,
Broken Window,200.00,Broken Window Replacement,
Damaged Countertop,250.00,Kitchen Countertop Repair,
Scratched Hardwood,180.00,Hardwood Floor Scratch Repair,
Broken Cabinet Door,120.00,Cabinet Door Replacement,
Damaged Light Fixture,85.00,Light Fixture Replacement,
Broken Toilet,175.00,Toilet Replacement,
Damaged Sink,150.00,Sink Replacement,
Broken Dishwasher,300.00,Dishwasher Replacement,`
